import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Send,
  Check,
  CalendarClock,
  Loader2,
  PartyPopper,
  Home,
  FileText,
  Info,
  MessageCircle,
  Mail,
  CheckCircle2,
  RotateCcw,
  Save,
  AlertTriangle,
} from 'lucide-react';
import Footer from '../components/layout/Footer';
import FormField, { normalizeOptions } from '../components/ps/FormField';
import { psConfig, psSections, psQuestions, isVisible } from '../data/processoSeletivoData';
import { enviarInscricao, supabaseConfigurado } from '../lib/supabase';

/* ==========================================================================
 *  Rascunho automático (localStorage)
 *  — mantém as respostas se a pessoa atualizar ou fechar a página
 *  — registra quem já enviou, para não preencher duas vezes
 * ========================================================================== */

const STORAGE_KEY = psConfig.storageKey || 'ati-ps';
const DRAFT_KEY = `${STORAGE_KEY}:rascunho`;
const SENT_KEY = `${STORAGE_KEY}:enviado`;

/* Em navegação anônima ou com cookies bloqueados o localStorage pode lançar
 * erro. Nesses casos o formulário continua funcionando, só sem salvar nada. */
const storage = {
  read(key) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  write(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* sem espaço ou sem permissão — segue sem salvar */
    }
  },
  clear(key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* idem */
    }
  },
};

/* ==========================================================================
 *  Helpers de máscara e validação
 * ========================================================================== */

const maskPhone = (raw) => {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.replace(/^(\d{0,2})/, '($1');
  if (digits.length <= 6) return digits.replace(/^(\d{2})(\d{0,4})/, '($1) $2');
  if (digits.length <= 10) return digits.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return digits.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

const isEmpty = (value) => {
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'boolean') return value === false;
  return value === undefined || value === null || String(value).trim() === '';
};

const validateQuestion = (question, rawValue) => {
  const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

  if (question.required && isEmpty(value)) {
    if (question.type === 'consent') return 'É preciso marcar esta confirmação para enviar.';
    if (question.type === 'checkbox') return 'Marque pelo menos uma opção.';
    return 'Esta pergunta é obrigatória.';
  }
  if (isEmpty(value)) return null; // opcional e vazio → tudo certo

  switch (question.type) {
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) ? null : 'Digite um e-mail válido.';
    case 'tel':
      return value.replace(/\D/g, '').length >= 10
        ? null
        : 'Digite um telefone válido com DDD.';
    case 'number': {
      const n = Number(value);
      if (Number.isNaN(n)) return 'Digite apenas números.';
      if (question.min !== undefined && n < question.min)
        return `O valor mínimo é ${question.min}.`;
      if (question.max !== undefined && n > question.max)
        return `O valor máximo é ${question.max}.`;
      return null;
    }
    case 'url': {
      const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
      try {
        const url = new URL(withProtocol);
        return url.hostname.includes('.') ? null : 'Digite um link válido.';
      } catch {
        return 'Digite um link válido.';
      }
    }
    case 'radio':
    case 'select': {
      const allowed = normalizeOptions(question.options).map((o) => o.value);
      return allowed.includes(value) ? null : 'Selecione uma das opções.';
    }
    case 'checkbox': {
      const allowed = normalizeOptions(question.options).map((o) => o.value);
      return value.every((v) => allowed.includes(v)) ? null : 'Seleção inválida.';
    }
    default:
      return null;
  }
};

/* ==========================================================================
 *  Cabeçalho enxuto da página (sem a navegação do site)
 * ========================================================================== */

const PsHeader = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-night-900/[0.97] border-b border-white/[0.07] backdrop-blur-sm">
    <div className="w-full px-5 sm:px-8 lg:px-12 py-3.5 flex items-center justify-between gap-6">
      <Link to="/" className="flex items-center gap-3 group shrink-0" aria-label="AgroTech Inteli">
        <img
          src="/logo-light.png"
          alt="AgroTech Inteli"
          className="h-10 md:h-11 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </Link>
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm font-medium text-mist-300 hover:text-primary-400 transition-colors"
      >
        <ArrowLeft size={16} />
        <span className="hidden sm:inline">Voltar para o site</span>
        <span className="sm:hidden">Voltar</span>
      </Link>
    </div>
  </header>
);

/* ==========================================================================
 *  Tela de sucesso
 * ========================================================================== */

const SuccessScreen = () => (
  <div className="min-h-screen flex items-center justify-center px-5 py-32">
    <div className="relative w-full max-w-xl text-center">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[32rem] h-[32rem] rounded-full bg-primary-500/[0.13] blur-3xl pointer-events-none" />
      <div className="relative card-surface px-8 py-14 md:px-12">
        <div className="mx-auto mb-8 h-20 w-20 rounded-full bg-primary-400 text-night-950 flex items-center justify-center shadow-[0_20px_50px_-15px_rgba(74,222,128,0.7)]">
          <PartyPopper size={34} />
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-mist-50 tracking-tight">
          {psConfig.success.title}
        </h1>
        <p className="mt-5 text-mist-400 leading-relaxed">{psConfig.success.message}</p>
        {psConfig.success.footnote && (
          <p className="mt-5 text-sm text-mist-500 leading-relaxed border-t border-white/[0.07] pt-5">
            {psConfig.success.footnote}
          </p>
        )}
        <Link to="/" className="btn-primary mt-10">
          <Home size={17} />
          Voltar para o site
        </Link>
      </div>
    </div>
  </div>
);

/* ==========================================================================
 *  Botões de contato da presidência (reaproveitados em telas diferentes)
 * ========================================================================== */

const ContatoLinks = ({ className = '' }) => {
  const { contato } = psConfig;
  if (!contato || (!contato.whatsapp && !contato.email)) return null;

  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {contato.whatsapp && (
        <a
          href={`https://wa.me/55${contato.whatsapp.replace(/\D/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary py-3 px-6 text-sm"
        >
          <MessageCircle size={16} />
          WhatsApp
        </a>
      )}
      {contato.email && (
        <a href={`mailto:${contato.email}`} className="btn-secondary py-3 px-6 text-sm">
          <Mail size={16} />
          E-mail
        </a>
      )}
    </div>
  );
};

/* ==========================================================================
 *  Tela de "você já se inscreveu"
 * ========================================================================== */

const AlreadySubmittedScreen = ({ registro }) => {
  const textos = psConfig.jaEnviado || {};
  const enviadoEm = registro?.submitted_at
    ? new Date(registro.submitted_at).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-32">
      <div className="relative w-full max-w-xl text-center">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[32rem] h-[32rem] rounded-full bg-primary-500/[0.1] blur-3xl pointer-events-none" />
        <div className="relative card-surface px-8 py-14 md:px-12">
          <div className="mx-auto mb-8 h-20 w-20 rounded-full bg-primary-400/15 text-primary-300 border border-primary-400/30 flex items-center justify-center">
            <CheckCircle2 size={34} />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-mist-50 tracking-tight">
            {textos.title}
          </h1>
          <p className="mt-5 text-mist-400 leading-relaxed">{textos.message}</p>

          {(registro?.nome_completo || enviadoEm) && (
            <div className="mt-7 rounded-2xl bg-white/[0.03] border border-white/[0.07] px-5 py-4 text-sm text-mist-400 space-y-1">
              {registro?.nome_completo && (
                <p>
                  Inscrição de{' '}
                  <strong className="text-mist-100 font-semibold">{registro.nome_completo}</strong>
                </p>
              )}
              {enviadoEm && <p>Enviada em {enviadoEm}</p>}
            </div>
          )}

          {textos.footnote && (
            <p className="mt-6 text-sm text-mist-500 leading-relaxed">{textos.footnote}</p>
          )}

          <ContatoLinks className="mt-6 justify-center" />

          <div className="mt-8 pt-7 border-t border-white/[0.07]">
            <Link to="/" className="btn-ghost">
              <Home size={16} />
              Voltar para o site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================================================================
 *  Página do formulário
 * ========================================================================== */

const ProcessoSeletivo = () => {
  // Rascunho salvo no navegador (se houver) é carregado já na primeira renderização
  const rascunho = useRef(storage.read(DRAFT_KEY)).current;

  const [formData, setFormData] = useState(() => rascunho?.formData || {});
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(() => rascunho?.step || 0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [erroEnvio, setErroEnvio] = useState(null);
  // Inscrição já enviada por este navegador (lido uma vez, na montagem)
  const jaEnviado = useRef(storage.read(SENT_KEY)).current;
  const [rascunhoRestaurado, setRascunhoRestaurado] = useState(
    () => !!rascunho && Object.keys(rascunho.formData || {}).length > 0
  );
  const formTopRef = useRef(null);

  useEffect(() => {
    document.title = 'Inscrições | AgroTech Inteli';
  }, []);

  /* --- Salva o rascunho a cada alteração ------------------------------- */
  useEffect(() => {
    if (isDone || jaEnviado) return;
    if (Object.keys(formData).length === 0) return;
    storage.write(DRAFT_KEY, { formData, step, savedAt: new Date().toISOString() });
  }, [formData, step, isDone, jaEnviado]);

  /* --- Descartar o rascunho e recomeçar do zero ------------------------- */
  const descartarRascunho = () => {
    storage.clear(DRAFT_KEY);
    setFormData({});
    setErrors({});
    setStep(0);
    setRascunhoRestaurado(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* --- Seções e perguntas visíveis dependem das respostas (lógica showIf) --- */
  const visibleSections = useMemo(
    () =>
      psSections
        .filter((sec) => isVisible(sec.showIf, formData))
        .map((sec) => ({
          ...sec,
          questions: sec.questions.filter((q) => isVisible(q.showIf, formData)),
        })),
    [formData]
  );

  const totalSteps = visibleSections.length;
  const safeStep = Math.min(step, totalSteps - 1);
  const section = visibleSections[safeStep];
  const isLastStep = safeStep === totalSteps - 1;

  useEffect(() => {
    if (step > totalSteps - 1) setStep(totalSteps - 1);
  }, [step, totalSteps]);

  // Progresso baseado nas perguntas obrigatórias visíveis já respondidas
  const progress = useMemo(() => {
    const required = visibleSections.flatMap((sec) => sec.questions.filter((q) => q.required));
    if (!required.length) return 0;
    const filled = required.filter((q) => !isEmpty(formData[q.id])).length;
    return Math.round((filled / required.length) * 100);
  }, [visibleSections, formData]);

  const handleChange = useCallback((id, rawValue) => {
    const question = psQuestions.find((q) => q.id === id);
    const value = question?.type === 'tel' ? maskPhone(rawValue) : rawValue;

    setFormData((prev) => ({ ...prev, [id]: value }));
    setErroEnvio(null);
    setErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const validateSection = (targetSection) => {
    const found = {};
    targetSection.questions.forEach((q) => {
      const message = validateQuestion(q, formData[q.id]);
      if (message) found[q.id] = message;
    });
    return found;
  };

  const scrollToTop = () => {
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const focusFirstError = (found) => {
    const firstId = Object.keys(found)[0];
    const el = document.getElementById(firstId) || document.getElementsByName(firstId)[0];
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (el && typeof el.focus === 'function') {
      setTimeout(() => el.focus({ preventScroll: true }), 400);
    }
  };

  const handleNext = () => {
    const found = validateSection(section);
    setErrors(found);
    if (Object.keys(found).length) return focusFirstError(found);
    setStep(Math.min(safeStep + 1, totalSteps - 1));
    scrollToTop();
  };

  const handleBack = () => {
    setStep(Math.max(safeStep - 1, 0));
    scrollToTop();
  };

  const goToStep = (index) => {
    if (index === safeStep) return;
    // Voltar é sempre livre; avançar exige a etapa atual válida
    if (index < safeStep) {
      setStep(index);
      return scrollToTop();
    }
    const found = validateSection(section);
    setErrors(found);
    if (Object.keys(found).length) return focusFirstError(found);
    setStep(index);
    scrollToTop();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Valida todas as seções visíveis antes de enviar
    const allErrors = {};
    let firstInvalidStep;
    visibleSections.forEach((sec, index) => {
      const found = validateSection(sec);
      if (Object.keys(found).length && firstInvalidStep === undefined) firstInvalidStep = index;
      Object.assign(allErrors, found);
    });

    if (Object.keys(allErrors).length) {
      setErrors(allErrors);
      if (firstInvalidStep !== undefined && firstInvalidStep !== safeStep) {
        setStep(firstInvalidStep);
        scrollToTop();
      } else {
        focusFirstError(allErrors);
      }
      return;
    }

    // Payload já pronto para o Supabase (uma chave por pergunta).
    // Perguntas escondidas pela lógica de trilha são enviadas como null.
    const visibleIds = new Set(
      visibleSections.flatMap((sec) => sec.questions.map((q) => q.id))
    );

    const payload = psQuestions.reduce(
      (acc, q) => {
        if (!visibleIds.has(q.id)) {
          acc[q.id] = null;
          return acc;
        }
        let value = formData[q.id];
        if (typeof value === 'string') value = value.trim();
        if (q.type === 'url' && value && !/^https?:\/\//i.test(value)) {
          value = `https://${value}`;
        }
        if (q.type === 'number' && value !== undefined && value !== '') value = Number(value);
        if (q.type === 'consent') value = value === true;
        if (Array.isArray(value) && value.length === 0) value = null;
        acc[q.id] = value === '' || value === undefined ? null : value;
        return acc;
      },
      { submitted_at: new Date().toISOString() }
    );

    setIsSubmitting(true);
    setErroEnvio(null);

    try {
      await enviarInscricao(payload);
    } catch (erro) {
      // Falhou: o rascunho continua salvo, para a pessoa não perder nada
      setIsSubmitting(false);
      setErroEnvio(erro.message);

      if (erro.campo) {
        setErrors((prev) => ({ ...prev, [erro.campo]: erro.message }));
        const indice = visibleSections.findIndex((sec) =>
          sec.questions.some((q) => q.id === erro.campo)
        );
        if (indice >= 0 && indice !== safeStep) {
          setStep(indice);
          scrollToTop();
        } else {
          focusFirstError({ [erro.campo]: erro.message });
        }
      }
      return;
    }

    // Deu certo: apaga o rascunho e marca que este navegador já se inscreveu
    storage.clear(DRAFT_KEY);
    storage.write(SENT_KEY, {
      nome_completo: payload.nome_completo || null,
      email: payload.email || null,
      submitted_at: payload.submitted_at,
    });

    setIsSubmitting(false);
    setIsDone(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Quem já enviou não preenche de novo
  if (jaEnviado && !isDone) {
    return (
      <div className="min-h-screen bg-night-950">
        <PsHeader />
        <AlreadySubmittedScreen registro={jaEnviado} />
        <Footer />
      </div>
    );
  }

  if (isDone) {
    return (
      <div className="min-h-screen bg-night-950">
        <PsHeader />
        <SuccessScreen />
      </div>
    );
  }

  const { manuais, cronograma, contato } = psConfig;
  const temContato = contato && (contato.whatsapp || contato.email);

  return (
    <div className="min-h-screen bg-night-950">
      <PsHeader />

      <main className="relative overflow-hidden">
        {/* Fundo: grade verde + brilho */}
        <div className="absolute inset-x-0 top-0 h-[46rem] bg-grid-green bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_top,black_10%,transparent_70%)] opacity-70 pointer-events-none" />
        <div className="absolute -top-56 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-primary-700/[0.16] blur-3xl pointer-events-none" />

        {/* ---------------------------------------------------------- Hero */}
        <section className="relative container-custom pt-36 md:pt-44 pb-12">
          <div className="max-w-3xl">
            <span className="eyebrow">{psConfig.eyebrow}</span>
            <h1 className="section-title mt-5">
              {psConfig.title} <span className="text-gradient">{psConfig.titleHighlight}</span>
            </h1>
            <p className="section-subtitle mt-6">{psConfig.subtitle}</p>

            {psConfig.notice && (
              <div className="mt-7 flex items-start gap-3 rounded-2xl border border-primary-400/20 bg-primary-400/[0.06] px-5 py-4 max-w-2xl">
                <Info size={17} className="text-primary-400 shrink-0 mt-0.5" />
                <p className="text-sm text-mist-200 leading-relaxed">{psConfig.notice}</p>
              </div>
            )}

            {psConfig.deadline && (
              <div className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-primary-400/25 bg-primary-400/[0.08] px-5 py-2.5">
                <CalendarClock size={16} className="text-primary-400" />
                <span className="text-sm text-mist-200">
                  Inscrições abertas até{' '}
                  <strong className="text-mist-50 font-semibold">{psConfig.deadline}</strong>
                </span>
              </div>
            )}
          </div>

          {/* Manuais das trilhas */}
          {manuais?.items?.length > 0 && (
            <div className="mt-14 card-surface p-7 md:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                <div className="lg:col-span-6">
                  <span className="eyebrow">{manuais.title}</span>
                  <p className="mt-3 text-mist-400 leading-relaxed">{manuais.description}</p>
                </div>
                <div className="lg:col-span-6 grid sm:grid-cols-2 gap-3">
                  {manuais.items.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 rounded-2xl border border-white/[0.09] bg-white/[0.03] hover:bg-white/[0.07] hover:border-primary-400/50 px-5 py-4 transition-all duration-300"
                    >
                      <span className="h-10 w-10 shrink-0 rounded-xl bg-primary-400/15 text-primary-300 flex items-center justify-center group-hover:bg-primary-400 group-hover:text-night-950 transition-colors">
                        <FileText size={17} />
                      </span>
                      <span className="text-sm font-medium text-mist-100 leading-snug">
                        {item.label}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Cronograma */}
          {cronograma?.length > 0 && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {cronograma.map((item) => (
                <div key={item.label} className="ati-telemetry px-6 py-5">
                  <p className="text-[0.7rem] uppercase tracking-[0.18em] text-mist-500">
                    {item.label}
                  </p>
                  <p className="font-display text-2xl font-bold text-mist-50 mt-1.5">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Contato para dúvidas */}
          {temContato && (
            <div className="mt-5 card-surface p-6 md:px-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div>
                <p className="text-[0.7rem] uppercase tracking-[0.18em] text-mist-500">
                  Dúvidas durante o processo
                </p>
                <p className="mt-1.5 text-mist-50 font-semibold">{contato.nome}</p>
                <p className="text-sm text-mist-500">{contato.cargo}</p>
              </div>
              <ContatoLinks />
            </div>
          )}
        </section>

        {/* -------------------------------------------------------- Formulário */}
        <section ref={formTopRef} className="relative container-custom pb-28 scroll-mt-28">
          <div className="max-w-3xl mx-auto">
            {/* Aviso de rascunho recuperado */}
            {rascunhoRestaurado && (
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-primary-400/25 bg-primary-400/[0.06] px-5 py-4">
                <div className="flex items-start gap-3">
                  <Save size={17} className="text-primary-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-mist-200 leading-relaxed">
                    Recuperamos as respostas que você já tinha preenchido neste navegador. É só
                    continuar de onde parou.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={descartarRascunho}
                  className="shrink-0 inline-flex items-center gap-2 text-xs font-medium text-mist-400 hover:text-primary-400 transition-colors"
                >
                  <RotateCcw size={14} />
                  Recomeçar do zero
                </button>
              </div>
            )}

            {/* Etapas + barra de progresso */}
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-2.5 mb-5">
                {visibleSections.map((sec, index) => {
                  const isCurrent = index === safeStep;
                  const isPast = index < safeStep;
                  return (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => goToStep(index)}
                      className={
                        'group inline-flex items-center gap-2.5 rounded-full border pl-2 pr-4 py-1.5 text-sm transition-all duration-300 ' +
                        (isCurrent
                          ? 'border-primary-400/60 bg-primary-400/[0.1] text-mist-50'
                          : isPast
                          ? 'border-white/[0.12] bg-white/[0.04] text-mist-300 hover:border-primary-400/40'
                          : 'border-white/[0.08] bg-transparent text-mist-600 hover:text-mist-400')
                      }
                    >
                      <span
                        className={
                          'h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-[0.7rem] font-semibold transition-colors ' +
                          (isCurrent
                            ? 'bg-primary-400 text-night-950'
                            : isPast
                            ? 'bg-primary-400/25 text-primary-300'
                            : 'bg-white/[0.06] text-mist-500')
                        }
                      >
                        {isPast ? <Check size={12} strokeWidth={3.5} /> : index + 1}
                      </span>
                      <span className="font-medium whitespace-nowrap">{sec.title}</span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-4">
                <div className="h-1.5 flex-1 rounded-full bg-white/[0.07] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary-600 to-primary-400 transition-[width] duration-700 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs tabular-nums text-mist-500 shrink-0">
                  {progress}% preenchido
                </span>
              </div>
            </div>

            {/* Cartão do formulário */}
            <form onSubmit={handleSubmit} noValidate>
              <div className="card-surface p-7 md:p-10">
                <div className="pb-7 mb-8 border-b border-white/[0.07]">
                  <p className="text-xs uppercase tracking-[0.18em] text-primary-400 mb-2">
                    Etapa {safeStep + 1} de {totalSteps}
                  </p>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-mist-50 tracking-tight">
                    {section.title}
                  </h2>
                  {section.description && (
                    <p className="mt-2.5 text-mist-400 leading-relaxed">{section.description}</p>
                  )}
                  {section.intro?.length > 0 && (
                    <div className="mt-5 space-y-2.5 rounded-2xl bg-white/[0.03] border border-white/[0.06] px-5 py-4">
                      {section.intro.map((paragraph, i) => (
                        <p key={i} className="text-sm text-mist-400 leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-7">
                  {section.questions.map((question) => (
                    <FormField
                      key={question.id}
                      question={question}
                      value={formData[question.id]}
                      error={errors[question.id]}
                      onChange={handleChange}
                    />
                  ))}
                </div>

                {isLastStep && psConfig.privacyNotice && (
                  <p className="mt-9 text-xs text-mist-500 leading-relaxed border-t border-white/[0.07] pt-6">
                    {psConfig.privacyNotice}
                  </p>
                )}
              </div>

              {/* Falha no envio — a pessoa continua com tudo preenchido */}
              {erroEnvio && (
                <div
                  role="alert"
                  className="mt-5 flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/[0.08] px-5 py-4"
                >
                  <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-200">Não foi possível enviar</p>
                    <p className="text-sm text-mist-300 leading-relaxed mt-1">{erroEnvio}</p>
                  </div>
                </div>
              )}

              {/* Aviso de desenvolvimento: sem credenciais, nada é gravado */}
              {!supabaseConfigurado && isLastStep && (
                <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/[0.07] px-5 py-4">
                  <AlertTriangle size={17} className="text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-mist-300 leading-relaxed">
                    <strong className="text-amber-200 font-semibold">Modo de teste.</strong> O
                    Supabase ainda não está configurado, então esta inscrição não será gravada em
                    lugar nenhum. Veja o passo a passo em{' '}
                    <code className="text-mist-100">SUPABASE-SETUP.md</code>.
                  </p>
                </div>
              )}

              {/* Navegação */}
              <div className="mt-7 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
                {safeStep > 0 ? (
                  <button type="button" onClick={handleBack} className="btn-secondary">
                    <ArrowLeft size={17} />
                    Voltar
                  </button>
                ) : (
                  <span className="hidden sm:block" />
                )}

                {isLastStep ? (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={17} className="animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send size={17} />
                        Enviar inscrição
                      </>
                    )}
                  </button>
                ) : (
                  <button type="button" onClick={handleNext} className="btn-primary">
                    Continuar
                    <ArrowRight size={17} />
                  </button>
                )}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-2 text-xs text-mist-600">
                <span>
                  Campos marcados com <span className="text-primary-400">*</span> são obrigatórios.
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Save size={12} />
                  Suas respostas são salvas automaticamente neste navegador.
                </span>
              </div>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProcessoSeletivo;
